import { useQuery } from '@tanstack/react-query';
import {
  fetchScheduleFires,
  fetchSchedules,
  scheduleFiresQueryKey,
  schedulesQueryKey,
} from '../lib/api';
import type { ScheduleFire } from '../lib/api';
import { soonestArmed } from '../lib/schedule';
import { scheduleState } from '../lib/scheduleAliveness';
import type { ScheduleState } from '../lib/scheduleAliveness';

/**
 * The ● rhythm of a space, resolved from its soonest armed Schedule — the one
 * fetch behind BOTH the header's rest chip ("asleep · wakes 16:40") and its
 * amber confession ("16:40 request fired 2h ago — waiting for a machine").
 * react-query dedupes the two subscriptions by key, so sharing this hook keeps
 * the two surfaces reading the exact same fire without a second request.
 *
 * `null` means the space has no armed Schedule — it has no ● rhythm to speak of,
 * and the surfaces stay silent rather than inventing one.
 */
export function useSpaceScheduleState(spaceId: string): {
  state: ScheduleState | null;
  latestFire: ScheduleFire | undefined;
} {
  const { data } = useQuery({
    queryKey: schedulesQueryKey(spaceId),
    queryFn: () => fetchSchedules(spaceId),
  });
  const schedule = soonestArmed(data?.schedules);

  // A Schedule that has never run has no history to read; keep the fires query
  // keyed on a real schedule id (never a placeholder) so it can stay disabled.
  const { data: fireData } = useQuery({
    queryKey: scheduleFiresQueryKey(spaceId, schedule?.schedule_id ?? '∅'),
    queryFn: () => fetchScheduleFires(spaceId, schedule!.schedule_id),
    enabled: Boolean(schedule && schedule.last_fired_at !== null),
  });

  if (!schedule) return { state: null, latestFire: undefined };

  const latestFire = fireData?.fires[0];
  const state = scheduleState({
    enabled: true,
    nextFireAt: schedule.next_fire_at,
    latestFire,
  });
  return { state, latestFire };
}
